import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Input,
  Modal,
  Select,
  useToast,
} from '@adop/ui';
import QRCode from 'qrcode';
import {
  AlertTriangle,
  Copy,
  ExternalLink,
  Pencil,
  Plus,
  Printer,
  QrCode,
  RefreshCw,
  Trash2,
  UtensilsCrossed,
} from 'lucide-react';
import type { QrPaymentMode } from '@adop/shared';
import { useWorkspace } from '../../lib/workspace-context';
import { useAuth } from '../../lib/auth-context';
import { useAsync } from '../../lib/useAsync';
import {
  createTable,
  deleteTable,
  getLocation,
  listTables,
  updateLocationSeo,
  updateTable,
  type LocationTableRow,
} from '../../lib/data';
import {
  buildTableQrSheet,
  newTableQrToken,
  tableQrUrl,
} from '../../lib/table-qr';
import { storefrontOriginFor } from '../../lib/storefront';
import { errorMessage } from '../../lib/api';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { LoadingBlock } from '../../components/LoadingState';
import { QrCodeImage } from '../../components/QrCodeImage';

interface TableForm {
  id: string | null;
  label: string;
  seats: string;
}

interface PrintCard {
  label: string;
  seats: number | null;
  url: string;
  dataUrl: string;
}

const PAYMENT_MODES: Array<{ value: QrPaymentMode; label: string }> = [
  { value: 'pay_at_table', label: 'Pay at the table' },
  { value: 'online', label: 'Pay online (Stripe)' },
];

export function TablesPage() {
  const { location: workspaceLocation, restaurant, canManage } = useWorkspace();
  const { refresh } = useAuth();
  const { toast } = useToast();

  const locationState = useAsync(
    () => (workspaceLocation ? getLocation(workspaceLocation.id) : Promise.resolve(null)),
    [workspaceLocation?.id],
  );
  const tablesState = useAsync(
    () => (workspaceLocation ? listTables(workspaceLocation.id) : Promise.resolve([])),
    [workspaceLocation?.id],
  );
  const location = locationState.data;

  const [qrEnabled, setQrEnabled] = useState(false);
  const [paymentMode, setPaymentMode] = useState<QrPaymentMode>('pay_at_table');
  const [savingSettings, setSavingSettings] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [tableForm, setTableForm] = useState<TableForm | null>(null);
  const [qrTarget, setQrTarget] = useState<LocationTableRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LocationTableRow | null>(null);
  const [printCards, setPrintCards] = useState<PrintCard[] | null>(null);

  useEffect(() => {
    if (!location) return;
    setQrEnabled(location.qr_ordering_enabled);
    setPaymentMode((location.qr_payment_mode as QrPaymentMode) ?? 'pay_at_table');
  }, [location]);

  // The print sheet is rendered first, then window.print() runs after paint.
  useEffect(() => {
    if (!printCards) return;
    const clear = () => setPrintCards(null);
    window.addEventListener('afterprint', clear);
    const timer = window.setTimeout(() => window.print(), 120);
    return () => {
      window.removeEventListener('afterprint', clear);
      window.clearTimeout(timer);
    };
  }, [printCards]);

  const tables = useMemo(() => tablesState.data ?? [], [tablesState.data]);
  const activeTables = useMemo(() => tables.filter((table) => table.active), [tables]);
  const stripeConnected = restaurant?.stripe_charges_enabled ?? false;
  const showOnlineWarning = paymentMode === 'online' && !stripeConnected;

  if (!workspaceLocation) {
    return <EmptyState icon={<UtensilsCrossed className="h-6 w-6" />} title="Select a location" />;
  }
  if (!canManage) {
    return (
      <EmptyState
        icon={<QrCode className="h-6 w-6" />}
        title="Managers only"
        description="Ask an owner or manager to manage table QR codes."
      />
    );
  }
  if (locationState.loading || !location) {
    return <LoadingBlock />;
  }

  const saveQrSettings = async () => {
    setSavingSettings(true);
    setError(null);
    try {
      await updateLocationSeo(location.id, {
        qr_ordering_enabled: qrEnabled,
        qr_payment_mode: paymentMode,
      });
      toast({ title: 'Table ordering settings saved', variant: 'success' });
      locationState.reload();
      void refresh();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setSavingSettings(false);
    }
  };

  const saveTable = async () => {
    if (!tableForm) return;
    const label = tableForm.label.trim();
    if (label.length === 0) {
      toast({ title: 'Give the table a label', variant: 'error' });
      return;
    }
    const seats = tableForm.seats.trim() === '' ? null : Number(tableForm.seats);
    if (seats !== null && (!Number.isInteger(seats) || seats < 1)) {
      toast({ title: 'Seats must be a whole number of 1 or more', variant: 'error' });
      return;
    }
    setBusy(true);
    try {
      if (tableForm.id) {
        await updateTable(tableForm.id, { label, seats });
        toast({ title: 'Table updated', variant: 'success' });
      } else {
        await createTable({
          restaurantId: location.restaurant_id,
          locationId: location.id,
          label,
          seats,
          qrToken: newTableQrToken(),
        });
        toast({ title: 'Table added', variant: 'success' });
      }
      setTableForm(null);
      tablesState.reload();
    } catch (cause) {
      toast({ title: 'Could not save table', description: errorMessage(cause), variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (table: LocationTableRow) => {
    try {
      await updateTable(table.id, { active: !table.active });
      tablesState.reload();
    } catch (cause) {
      toast({ title: 'Could not update table', description: errorMessage(cause), variant: 'error' });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTable(deleteTarget.id);
      toast({ title: 'Table deleted', variant: 'success' });
      setDeleteTarget(null);
      tablesState.reload();
    } catch (cause) {
      toast({ title: 'Could not delete table', description: errorMessage(cause), variant: 'error' });
    }
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: 'Copied', variant: 'success' });
    } catch {
      toast({ title: 'Copy failed', variant: 'error' });
    }
  };

  const printAll = async () => {
    if (activeTables.length === 0) {
      toast({ title: 'Add an active table first', variant: 'error' });
      return;
    }
    setError(null);
    try {
      const sheet = buildTableQrSheet(
        restaurant?.name ?? '',
        { name: location.name, slug: location.slug },
        activeTables,
        storefrontOriginFor(location.slug),
      );
      const cards = await Promise.all(
        sheet.tables.map(async (table) => ({
          label: table.label,
          seats: table.seats,
          url: table.url,
          dataUrl: await QRCode.toDataURL(table.url, { width: 480, margin: 1 }),
        })),
      );
      setPrintCards(cards);
    } catch (cause) {
      setError(errorMessage(cause));
    }
  };

  const qrUrlFor = (table: LocationTableRow) =>
    tableQrUrl(location.slug, table.qr_token, storefrontOriginFor(location.slug));

  return (
    <div className="space-y-5">
      <PageHeader
        title="Tables"
        description="Guests scan a table QR to order from their phone. Orders flow into adop and your kitchen printer."
        action={
          <Button size="sm" onClick={() => setTableForm({ id: null, label: '', seats: '' })}>
            <Plus className="h-4 w-4" /> Add table
          </Button>
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      <Card>
        <CardHeader>
          <CardTitle>Table ordering</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Checkbox
            label="Enable QR table ordering at this location"
            checked={qrEnabled}
            onChange={(event) => setQrEnabled(event.target.checked)}
          />
          <div className="max-w-xs">
            <Select
              label="How guests pay"
              value={paymentMode}
              onChange={(event) => setPaymentMode(event.target.value as QrPaymentMode)}
            >
              {PAYMENT_MODES.map((mode) => (
                <option key={mode.value} value={mode.value}>
                  {mode.label}
                </option>
              ))}
            </Select>
          </div>
          {showOnlineWarning ? (
            <Alert variant="info">
              <div className="flex flex-wrap items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>
                  Online payment is selected but Stripe is not connected. Guests will not be able to pay
                  online until you connect it.
                </span>
                <Link to="/dashboard/payments" className="font-medium underline">
                  Connect Stripe →
                </Link>
              </div>
            </Alert>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <Button loading={savingSettings} onClick={() => void saveQrSettings()}>
              Save table ordering
            </Button>
            <Button variant="outline" size="sm" onClick={() => void locationState.reload()}>
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle>Tables</CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{activeTables.length} active</Badge>
              <Button variant="outline" size="sm" onClick={() => void printAll()}>
                <Printer className="h-4 w-4" /> Print all QRs
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {tablesState.loading ? (
            <div className="p-5">
              <LoadingBlock />
            </div>
          ) : tables.length === 0 ? (
            <EmptyState
              icon={<QrCode className="h-6 w-6" />}
              title="No tables yet"
              description="Add each table once, then print its QR code and put it on the table."
              className="border-0"
              action={
                <Button size="sm" onClick={() => setTableForm({ id: null, label: '', seats: '' })}>
                  <Plus className="h-4 w-4" /> Add table
                </Button>
              }
            />
          ) : (
            <ul className="divide-y divide-zinc-100">
              {tables.map((table) => (
                <li key={table.id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
                  <span className="font-medium text-zinc-900">{table.label}</span>
                  <Badge variant={table.active ? 'success' : 'default'}>
                    {table.active ? 'Active' : 'Inactive'}
                  </Badge>
                  <span className="text-zinc-500">
                    {table.seats ? `${table.seats} seats` : 'Seats not set'}
                  </span>
                  <div className="ml-auto flex items-center gap-1">
                    <Button variant="outline" size="sm" onClick={() => setQrTarget(table)}>
                      <QrCode className="h-4 w-4" /> Show QR
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Edit ${table.label}`}
                      onClick={() =>
                        setTableForm({
                          id: table.id,
                          label: table.label,
                          seats: table.seats === null ? '' : String(table.seats),
                        })
                      }
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Checkbox
                      label="Active"
                      checked={table.active}
                      onChange={() => void toggleActive(table)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Delete ${table.label}`}
                      onClick={() => setDeleteTarget(table)}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Modal
        open={tableForm !== null}
        onClose={() => setTableForm(null)}
        title={tableForm?.id ? 'Edit table' : 'Add table'}
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setTableForm(null)}>
              Cancel
            </Button>
            <Button loading={busy} onClick={() => void saveTable()}>
              Save table
            </Button>
          </>
        }
      >
        {tableForm ? (
          <div className="space-y-4">
            <Input
              label="Label"
              value={tableForm.label}
              placeholder="Table 1"
              onChange={(event) => setTableForm({ ...tableForm, label: event.target.value })}
            />
            <Input
              label="Seats (optional)"
              type="number"
              min={1}
              value={tableForm.seats}
              onChange={(event) => setTableForm({ ...tableForm, seats: event.target.value })}
            />
          </div>
        ) : null}
      </Modal>

      <Modal
        open={qrTarget !== null}
        onClose={() => setQrTarget(null)}
        title={qrTarget ? `QR for ${qrTarget.label}` : 'Table QR'}
        size="sm"
      >
        {qrTarget ? (
          <div className="flex flex-col items-center gap-3">
            <QrCodeImage
              value={qrUrlFor(qrTarget)}
              alt={`QR code for ${qrTarget.label}`}
              size={220}
            />
            <p className="break-all text-center text-xs text-zinc-500">{qrUrlFor(qrTarget)}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => void copy(qrUrlFor(qrTarget))}>
                <Copy className="h-4 w-4" /> Copy link
              </Button>
              <a
                href={qrUrlFor(qrTarget)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-zinc-300 px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
              >
                <ExternalLink className="h-4 w-4" /> Preview
              </a>
            </div>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={deleteTarget !== null}
        title={`Delete ${deleteTarget?.label ?? 'table'}?`}
        message="The QR code stops working. Past orders keep the table name they were placed with."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />

      {printCards ? (
        <div className="qr-print-sheet" aria-hidden="true">
          {printCards.map((card) => (
            <div key={card.url} className="qr-print-card">
              <p className="qr-print-restaurant">{restaurant?.name ?? ''}</p>
              <p className="qr-print-location">{location.name}</p>
              <h2 className="qr-print-table">{card.label}</h2>
              {card.seats ? <p className="qr-print-seats">{card.seats} seats</p> : null}
              <img src={card.dataUrl} alt="" className="qr-print-image" />
              <p className="qr-print-url">{card.url}</p>
              <p className="qr-print-instruction">Scan to order from your table.</p>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
